import {
  Component,
  Host,
  h,
  Prop,
  Watch,
  Method,
  Element,
} from '@stencil/core';
import type { CssClassMap } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-modal',
  styleUrl: 'cms-modal.scss',
  shadow: true,
})
export class CmsModal {
  /**
   * set Modal is open or closed
   */
  @Prop({ mutable: true }) open = false;
  /**
   * set label for close-button
   */
  @Prop({ mutable: true }) closeLabel = 'Close';

  /**
   * set the Content from outside in the end of the html body
   */
  @Prop({ mutable: true }) setModalContent: string;
  /**
   * set the headline from outside in the end of the html body
   */
  @Prop({ mutable: true }) setModalHeadline: string;

  @Element() host: HTMLElement;

  private closeButton: HTMLButtonElement;

  @Watch('setModalContent')
  private onSetModalContentChange() {
    return this.setModalContent;
  }
  @Watch('setModalHeadline')
  private onSetModalHeadlineChange() {
    return this.setModalHeadline;
  }
  @Watch('open')
  private(value: boolean) {
    if (value) {
      /* Focus modal */
      this.closeButton.focus({});
    }
  }

  @Method()
  async reset() {
    this.setModalContent = '';
    this.setModalHeadline = '';
  }

  private getCssClassMap(): CssClassMap {
    return {
      'cms-modal-overlay': true,
      'is-visible': this.open,
    };
  }

  componentDidLoad() {
    this.closeButton = this.host.querySelector('.cms-modal-close');
    window.addEventListener('keydown', (event) => {
      if (this.open && event.key === 'Escape') {
        this.closeModal();
      }
    });
  }

  private closeModal() {
    this.open = false;
  }

  render() {
    const dialogAttributes = this.setModalHeadline
      ? { 'aria-labelledby': 'modal-headline' }
      : {};

    return (
      <Host
        onClick={() => {
          this.closeModal();
        }}
      >
        <dialog
          class={this.getCssClassMap()}
          aria-modal='true'
          {...dialogAttributes}
        >
          <div class='cms-modal-inner'>
            <div class='cms-modal-headline-wrapper'>
              {this.setModalHeadline ? (
                <cms-headline
                  id='modal-headline'
                  headlineText={this.onSetModalHeadlineChange()}
                  headlineTag='h2'
                  headlinePosition='left'
                  layout='inline'
                  isAdaptive={false}
                  size='sm'
                />
              ) : (
                ''
              )}
              <button
                onClick={() => {
                  this.closeModal();
                }}
                type='button'
                class='cms-modal-close'
                aria-label={this.closeLabel}
                title={this.closeLabel}
              >
                <span class='close-text' aria-hidden='true'>
                  <cms-icon
                    icon='close'
                    size='sm'
                    colorToken='--cms-semanticColors-base-strong-text'
                  />
                </span>
              </button>
            </div>

            <div
              class='cms-modal-content'
              innerHTML={
                this.setModalContent ? this.onSetModalContentChange() : null
              }
            />
          </div>
        </dialog>
      </Host>
    );
  }
}
